<template>
	<div
		class="nb-wrap"
		:class="[`nb-${placement}`]"
		ref="wrap"
		@click.stop
	>
		<!-- 🔔 icona -->
		<div
			class="nb-bell"
			@click="onBellClick"
			:title="'Notifiche'"
		>
			<BellIcon class="ico" />
			<span
				v-if="unread > 0"
				class="badge"
				>{{ unread > 99 ? "99+" : unread }}</span
			>

			<!-- Dot come elemento separato -->
			<div
				v-if="unresolvedAlertsCount > 0"
				class="nb-dot alert"
			></div>
			<div
				v-else-if="unreadUpdatesCount > 0"
				class="nb-dot update"
			></div>
		</div>

		<!-- ▼ pannello: SIDEBAR -->
		<div
			v-if="open && placement === 'sidebar'"
			class="nb-panel is-fixed"
			:class="{ 'desk-open': isDesktop?.valueOf && sidebarOpen?.valueOf }"
			ref="panel"
		>
			<header class="np-hdr">
				<nav class="np-tabs">
					<button
						class="np-tab"
						:class="{ active: view === 'alerts' }"
						@click="view = 'alerts'"
						title="Avvisi"
					>
						Avvisi
						<span
							v-if="unreadLoadedAlert > 0"
							class="pill"
							>{{ unreadLoadedAlert }}</span
						>
					</button>
					<button
						class="np-tab"
						:class="{ active: view === 'updates' }"
						@click="view = 'updates'"
						title="Aggiornamenti"
					>
						Aggiornamenti
						<span
							v-if="unreadLoadedUpds > 0"
							class="pill"
							>{{ unreadLoadedUpds }}</span
						>
					</button>
				</nav>

				<div class="np-actions" style="display: flex; gap: 4px; align-items: center;">
					<button
						v-if="unread > 0"
						class="np-act"
						title="Segna TUTTE come lette"
						@click="handleMarkAllGlobal"
					>
						<MarkAsReadIcon class="ico" />
					</button>
					<button
						class="np-act"
						:aria-disabled="isMarkAllDisabled"
						:class="{ disabled: isMarkAllDisabled }"
						:title="markAllTitle"
						@click="!isMarkAllDisabled && markAllInCurrentViewAsRead()"
					>
					<component
						:is="isMarkAllDisabled ? MailIcon : MailOpenIcon"
						class="ico"
					/>
					</button>
				</div>
			</header>

			<ul class="np-list">
				<li
					v-for="n in currentList"
					:key="n.id"
					:class="[
						'np-item',
						n.severity,
						{
							update: !isAlert(n),
							unread: !isResolved(n), // dot e stile “unread” solo se NON risolto
							'n-read-flag': isResolved(n), // stile neutro se risolto/letto
							resolved: isResolved(n),
						},
					]"
					@click="handleItemClick(n)"
				>
					<div class="np-title">{{ n.title || "Aggiornamento" }}</div>
					<div class="np-msg">{{ n.message || "—" }}</div>
					<div class="np-meta">
						<template v-if="state.isAdmin">
							· <span v-if="n.email">{{ n.email }}</span>
							<span v-if="n.graph_name || n.graph_id">
								· {{ n.graph_name || "#" + n.graph_id }}<span v-if="n.graph_status"> ({{ n.graph_status }})</span></span
							>
						</template>
						· {{ formatWhen(n) }}
					</div>
					<button
						v-if="!n.is_read"
						class="n-btn"
						@click.stop="markOneAsRead(n.id)"
					>
						{{ view === "alerts" ? "Segna come già risolto" : "Segna come già letto" }}
					</button>
				</li>

				<li
					v-if="currentList.length === 0"
					class="np-empty"
				>
					{{ view === "alerts" ? "Nessun avviso" : "Nessun aggiornamento" }}
				</li>

				<!-- Carica altre: distinto per tab -->
				<li
					v-if="view === 'alerts' ? canLoadMoreAlerts : canLoadMoreUpdates"
					class="np-loadmore"
				>
					<button
						class="np-loadmore-btn"
						@click.stop="loadMore"
					>
						Carica altri {{ view === "alerts" ? "avvisi" : "aggiornamenti" }}
					</button>
				</li>
			</ul>
		</div>

		<!-- ▼ pannello: HEADER/BREADCRUMB -->
		<Teleport to="body">
			<div
				v-if="open && placement !== 'sidebar'"
				class="nb-panel is-floating"
				:style="floatStyle"
				ref="panel"
				@click.stop
			>
				<header class="np-hdr">
					<nav class="np-tabs">
						<button
							class="np-tab"
							:class="{ active: view === 'alerts' }"
							@click="view = 'alerts'"
							title="Avvisi"
						>
							Avvisi
							<span
								v-if="unreadLoadedAlert > 0"
								class="pill"
								>{{ unreadLoadedAlert }}</span
							>
						</button>
						<button
							class="np-tab"
							:class="{ active: view === 'updates' }"
							@click="view = 'updates'"
							title="Aggiornamenti"
						>
							Aggiornamenti
							<span
								v-if="unreadLoadedUpds > 0"
								class="pill"
								>{{ unreadLoadedUpds }}</span
							>
						</button>
					</nav>

					<div class="np-actions" style="display: flex; gap: 4px; align-items: center;">
						<button
							v-if="unread > 0"
							class="np-act"
							title="Segna TUTTE come lette"
							@click="handleMarkAllGlobal"
						>
							<MarkAsReadIcon class="ico" />
						</button>
						<button
							class="np-act"
							:aria-disabled="isMarkAllDisabled"
							:class="{ disabled: isMarkAllDisabled }"
							:title="markAllTitle"
							@click="!isMarkAllDisabled && markAllInCurrentViewAsRead()"
						>
						<component
							:is="isMarkAllDisabled ? MailIcon : MailOpenIcon"
							class="ico"
						/>
						</button>
					</div>
				</header>

				<ul class="np-list">
					<li
						v-for="n in currentList"
						:key="n.id"
						:class="[
							'np-item',
							n.severity,
							{
								update: !isAlert(n),
								unread: !isResolved(n), // dot e stile “unread” solo se NON risolto
								'n-read-flag': isResolved(n), // stile neutro se risolto/letto
								resolved: isResolved(n),
							},
						]"
						@click="handleItemClick(n)"
					>
						<div class="np-title">{{ n.title || "Aggiornamento" }}</div>
						<div class="np-msg">{{ n.message || "-" }}</div>
						<div class="np-meta">
							<template v-if="state.isAdmin">
								· <span v-if="n.email">{{ n.email }}</span>
								<span v-if="n.graph_name || n.graph_id">
									· {{ n.graph_name || "#" + n.graph_id }}<span v-if="n.graph_status"> ({{ n.graph_status }})</span></span
								>
							</template>
							· {{ formatWhen(n) }}
						</div>
						<button
							v-if="!n.is_read"
							class="n-btn"
							@click.stop="markOneAsRead(n.id)"
						>
							{{ view === "alerts" ? "Segna come già risolto" : "Segna come già letto" }}
						</button>
					</li>

					<li
						v-if="currentList.length === 0"
						class="np-empty"
					>
						{{ view === "alerts" ? "Nessun avviso" : "Nessun aggiornamento" }}
					</li>

					<!-- Carica altre: distinto per tab -->
					<li
						v-if="view === 'alerts' ? canLoadMoreAlerts : canLoadMoreUpdates"
						class="np-loadmore"
					>
						<button
							class="np-loadmore-btn"
							@click.stop="loadMore"
						>
							Carica altri {{ view === "alerts" ? "avvisi" : "aggiornamenti" }}
						</button>
					</li>
				</ul>
			</div>
		</Teleport>
	</div>
</template>

<script setup lang="ts">
	import { bootstrapNotifications, fetchLatest, markAllAsRead, markOneAsRead, setMaxLimit, useNotifications } from "@/composables/useNotifications";
	import router from "@/router";
	import { Bell as BellIcon, CheckCheck as MarkAsReadIcon, Mail as MailIcon, MailOpen as MailOpenIcon } from "lucide-vue-next";
	import { computed, inject, onMounted, onUnmounted, ref, type Ref } from "vue";

	const props = withDefaults(
		defineProps<{
			placement?: "sidebar" | "header" | "breadcrumb";
		}>(),
		{
			placement: "sidebar",
		}
	);
	const placement = props.placement;

	const { state, toggleOpen, close } = useNotifications();
	const items = computed(() => state.items);
	const unread = computed(() => state.unread);
	const open = computed(() => state.open);

	/** ▸ Limite globale fetch lato server */
	const currentLimit = ref(10); // es. parti da 10
	const unreadTotal = computed<number>(() => (state as any).unreadCount ?? unread.value ?? 0);

	/** ▸ Limiti di visualizzazione locali (slicing solo UI) */
	const alertsVisible = ref(5);
	const updatesVisible = ref(5);

	// iniettati
	const isDesktop = inject<Ref<boolean> | undefined>("isDesktop");
	const sidebarOpen = inject<Ref<boolean> | undefined>("isSidebarOpen");

	const wrap = ref<HTMLDivElement | null>(null);
	const PANEL_WIDTH = 320;

	/** Tabs */
	const view = ref<"alerts" | "updates">("alerts");

	/** Liste base (sul dataset fetchato) */
	const alerts = computed(() => items.value.filter((n) => n?.severity === "error" || n?.severity === "warning" || n?.payload?.kind === "alert"));
	const updates = computed(() => items.value.filter((n) => !alerts.value.some((a) => a.id === n.id)));

	/** Liste mostrate (slicing per-tab) */
	const alertsShown = computed(() => alerts.value.slice(0, alertsVisible.value));
	const updatesShown = computed(() => updates.value.slice(0, updatesVisible.value));
	const currentList = computed(() => (view.value === "alerts" ? alertsShown.value : updatesShown.value));

	// NON LETTI caricati (dataset corrente)
	const unreadLoaded = computed(() => items.value.filter((n) => !n.is_read).length);
	const unreadLoadedAlert = computed(() => alerts.value.filter((n) => !n.is_read).length);
	const unreadLoadedUpds = computed(() => updates.value.filter((n) => !n.is_read).length);

	// NON LETTI visibili nella lista mostrata
	const unreadShownAlert = computed(() => alertsShown.value.filter((n) => !n.is_read).length);
	const unreadShownUpds = computed(() => updatesShown.value.filter((n) => !n.is_read).length);

	// Posso mostrare di più in **questa tab** senza rifetchare?
	// (solo se esistono altri NON LETTI già fetchati ma ancora nascosti)
	const canShowMoreAlertsLocal = computed(() => unreadLoadedAlert.value > unreadShownAlert.value);
	const canShowMoreUpdatesLocal = computed(() => unreadLoadedUpds.value > unreadShownUpds.value);

	// Ha senso chiedere più dati al server? (ci sono NON LETTI fuori dal dataset)
	const canFetchMoreServer = computed(() => items.value.length === currentLimit.value && unreadTotal.value > unreadLoaded.value);

	// Stato bottone per tab corrente (prima prova ad allargare localmente gli UNREAD,
	// altrimenti – se esistono altri non letti fuori dal dataset – abilita fetch)
	const canLoadMoreAlerts = computed(() => canShowMoreAlertsLocal.value || canFetchMoreServer.value);
	const canLoadMoreUpdates = computed(() => canShowMoreUpdatesLocal.value || canFetchMoreServer.value);

	const isMarkAllDisabled = computed(() => (view.value === "alerts" ? unreadLoadedAlert.value : unreadLoadedUpds.value) === 0);

	const markAllTitle = computed(() =>
		isMarkAllDisabled.value ? (view.value === "alerts" ? "Nessun avviso da risolvere" : "Nessun aggiornamento da leggere") : view.value === "alerts" ? "Segna tutti come già risolti" : "Segna tutti come già letti"
	);

	const isAlert = (n: any) => n?.severity === "error" || n?.severity === "warning" || n?.payload?.kind === "alert";

	/** Considera "risolto" se è stato letto o ha uno di questi flag di risoluzione */
	const isResolved = (n: any) => !!(n?.is_read || n?.resolved || n?.payload?.resolved || n?.status === "resolved" || n?.graph_status === "ok");

	const unresolvedAlerts = computed(() => items.value.filter((n) => isAlert(n) && !isResolved(n)));

	const unresolvedAlertsCount = computed(() => unresolvedAlerts.value.length);

	const unreadUpdatesCount = computed(() => updates.value.filter((n) => !n.is_read).length);

	/** (opzionale) severità peggiore per colorare il dot */
	const worstUnresolvedSeverity = computed<"error" | "warning" | null>(() => {
		if (unresolvedAlerts.value.some((n) => n.severity === "error")) return "error";
		if (unresolvedAlerts.value.some((n) => n.severity === "warning")) return "warning";
		return null;
	});

	function onBellClick() {
		toggleOpen();
	}

	/** Handler “Carica altre” per tab corrente */
	async function loadMore() {
		if (view.value === "alerts") {
			if (canShowMoreAlertsLocal.value) {
				alertsVisible.value += 5;
				return;
			}
		} else {
			if (canShowMoreUpdatesLocal.value) {
				updatesVisible.value += 5;
				return;
			}
		}
		// qui non abbiamo più elementi locali → chiediamo al server altri item
		currentLimit.value += 10; // fetcha altri 10 (o 20 se preferisci)
		setMaxLimit(currentLimit.value);
		await fetchLatest({ limit: currentLimit.value });
		// piccolo boost al visibile per la tab corrente (in caso arrivino nuovi elementi della categoria)
		if (view.value === "alerts") alertsVisible.value += 5;
		else updatesVisible.value += 5;
	}

	/** Segna letti SOLO quelli della vista corrente (alert/update) */
	async function markAllInCurrentViewAsRead() {
		const base = view.value === "alerts" ? alertsShown.value : updatesShown.value;
		const ids = base.filter((n) => !n.is_read).map((n) => n.id);
		if (!ids.length) return;
		await Promise.all(ids.map((id) => markOneAsRead(id)));
		await fetchLatest({ limit: currentLimit.value });
	}

	function formatWhen(n: any) {
		const ts = n.createdAt ?? n.created_at;
		try {
			return ts ? new Date(ts).toLocaleString() : "";
		} catch {
			return "";
		}
	}

	async function handleItemClick(n: any) {
		if (!n.is_read) await markOneAsRead(n.id);
		if (n.graph_id) return;
	}

	/** Floating position */
	const floatStyle = computed(() => {
		const bellEl = wrap.value?.querySelector(".nb-bell") as HTMLElement | null;
		if (!bellEl) return { position: "fixed", top: "56px", right: "16px", zIndex: 20010 } as any;
		const r = bellEl.getBoundingClientRect();
		const gap = 8;
		let left = Math.round(r.right - PANEL_WIDTH);
		left = Math.max(8, Math.min(left, window.innerWidth - PANEL_WIDTH - 8));
		const top = Math.round(r.bottom + gap);
		return { position: "fixed", left: `${left}px`, top: `${top}px`, zIndex: 20010 } as any;
	});

	function onBodyClick() {
		close();
	}

	async function handleMarkAllGlobal() {
		try {
			await markAllAsRead();
			// refresh lista
			await fetchLatest({ limit: currentLimit.value });
		} catch (e) {
			console.error("Errore markAllAsRead:", e);
		}
	}

	onMounted(async () => {
		// Imposta il limite solo se il singleton non è ancora stato inizializzato:
		// evita di abbassare un limit già alzato da AlertsPage o da un altro componente.
		if (!state.ready) setMaxLimit(currentLimit.value);
		// fetchLatest({ limit: currentLimit.value });
		await bootstrapNotifications();

		document.body.addEventListener("click", onBodyClick);
	});
	onUnmounted(() => {
		document.body.removeEventListener("click", onBodyClick);
	});
</script>

<style scoped src="../assets/css/bell.css"></style>
